// Founder-benchmark authoring schema. ADDITIVE and OFF the exam wire path:
// these types never appear in ArmSpec, ExamTask, or the invoke request — they describe the
// offline event-graph + sessions + state-check DSL the reducer/compiler consume.

export type FounderEventKind =
  | "incorporation" | "raise" | "spend_change" | "revenue_change"
  | "hire" | "departure" | "churn_event" | "pivot" | "decision" | "milestone";

export type FounderEventPayload =
  | { kind: "incorporation"; baseCurrency: "USD" | "EUR"; foundingShares: number }
  | { kind: "raise"; instrument: "SAFE" | "priced" | "note"; amount: number; preMoney?: number;
      investor: string; sharesIssued?: number }       // priced rounds MUST set sharesIssued
  | { kind: "spend_change"; monthlyBurnDelta: number } // signed monthly net-cash-burn change
  | { kind: "revenue_change"; monthlyRevenueDelta: number }
  | { kind: "hire"; role: string; fullTime: boolean; cashCostMonthly: number; equityShares?: number }
  | { kind: "departure"; ofEventId: string }
  | { kind: "churn_event"; arrDelta: number; accountsLost: number }  // arrDelta signed (negative = churn loss)
  | { kind: "pivot"; from: string; to: string }
  | { kind: "decision"; label: string; supersedesEventId?: string }
  | { kind: "milestone"; label: string };

export interface FounderEvent {
  id: string;                 // stable, e.g. "ev-seed-raise"
  kind: FounderEventKind;
  at: string;                 // ISO-Z SIMULATED event date (never wall-clock)
  revealedAtSession: number;  // session index where the founder FIRST surfaces this (finding 1 spreading)
  causes?: string[];          // causal DAG edges
  token: string;              // unguessable recall token, e.g. "RNWY-7QK2"
  payload: FounderEventPayload;
  narrative: string;          // founder-voice fact the user-sim surfaces in-session
}

export interface FounderHistory {
  historyKey: string;         // == ArmState {imported:<historyKey>}
  schemaVersion: "founder-eg-1";
  persona: { name: string; company: string; sector: string; stage: string; traits: string[] };
  events: FounderEvent[];     // causal DAG, time-ordered by `at`
}

export interface FounderSession {
  index: number;              // 1..N (~30–50 for the smoke history)
  simulatedNow: string;       // ISO-Z THE pinned "now" for this session (finding 3) — monotonic increasing
  openThreads: string[];      // unresolved decisions carried in (continuity fodder)
  eventsSurfacedHere: string[]; // event ids the user-sim raises THIS session (finding 1 spreading)
}

export interface FounderSessions { historyKey: string; sessions: FounderSession[]; }

// State-check DSL. Band is filled BY THE COMPILER from reduceState — never hand-typed.
export type StateCheckOp = "in_band" | "equals_int" | "absent";

export type StateCheckField =
  | "runwayMonths" | "cashOnHand" | "headcount.fullTime" | "arr"
  | { capPct: string } | { capShares: string };   // EXACTLY ONE field per check

export interface StateCheck {
  id: string;
  field: StateCheckField;
  op: StateCheckOp;
  band?: { lo: number; hi: number };  // filled by the compiler from reduceState; never hand-typed
  expected?: number;                  // for equals_int (exact integer metrics)
  token: string;                      // makes the check brain-recall-checkable
  unit?: "months" | "USD" | "FTE" | "%";
}
